import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowPathIcon, TrophyIcon, ExclamationTriangleIcon, CheckCircleIcon, DocumentArrowDownIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { listHealthPromotionImportsByCompetence } from '../services/healthPromotionImportsService';
import type { HealthPromotionImportDocument } from '../services/healthPromotionTypes';
import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';
import { useToast } from '@/hooks/useToast';

type FlattenedRecord = {
  code: string;
  name: string;
  email: string;
  planName: string;
  status: string;
  amount: number;
  collaboratorId: string | null;
};

export function HealthPromotionImportDetailsPage() {
  const { competence = '' } = useParams();
  const { error: showError } = useToast();
  const [imports, setImports] = useState<HealthPromotionImportDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const data = await listHealthPromotionImportsByCompetence(competence);
        if (!active) return;
        setImports(data);
        if (!data.length) {
          setLoadError('Nenhuma importação encontrada para esta competência.');
        }
      } catch (error) {
        console.error('[HealthPromotionImportDetailsPage] failed to load imports', error);
        if (!active) return;
        setLoadError('Não foi possível carregar a importação.');
        showError('Não foi possível carregar a importação.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    if (!competence) {
      setImports([]);
      setLoading(false);
      setLoadError('Competência não encontrada.');
      return () => {
        active = false;
      };
    }

    void fetchDetails();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competence]);

  const flattenedRecords = useMemo<FlattenedRecord[]>(() => {
    const rows: FlattenedRecord[] = [];
    imports.forEach((importDoc) => {
      importDoc.recordsPreview.forEach((record: any) => {
        rows.push({
          code: typeof record?.code === 'string' ? record.code : '—',
          name: typeof record?.name === 'string' ? record.name : 'Sem nome',
          email: typeof record?.email === 'string' ? record.email : '—',
          planName: typeof record?.planName === 'string' ? record.planName : 'Plano',
          status: typeof record?.status === 'string' ? record.status : '—',
          amount: typeof record?.amount === 'number' ? record.amount : 0,
          collaboratorId: typeof record?.collaboratorId === 'string' ? record.collaboratorId : null,
        });
      });
    });
    return rows.sort((a, b) => {
      if (b.amount !== a.amount) return b.amount - a.amount;
      return a.name.localeCompare(b.name, 'pt-BR');
    });
  }, [imports]);

  const summary = useMemo(() => {
    return flattenedRecords.reduce(
      (acc, record) => {
        acc.totalAmount += record.amount ?? 0;
        acc.totalSeats += 1;
        if (record.collaboratorId) {
          acc.matchedCollaborators.add(record.collaboratorId);
        }
        return acc;
      },
      { totalAmount: 0, totalSeats: 0, matchedCollaborators: new Set<string>() },
    );
  }, [flattenedRecords]);

  const auditItems = useMemo(() => {
    return imports.flatMap((doc) => doc.audit || []);
  }, [imports]);

  const handleExportPdf = () => {
    const doc = new jsPDF();
    doc.text(`Auditoria de Promoção à Saúde - ${competence}`, 14, 10);

    const tableData = auditItems.map((item) => [
      item.beneficiaryName,
      formatCurrency(item.value ?? 0),
      item.reason,
    ]);

    autoTable(doc, {
      head: [['Beneficiário', 'Valor', 'Motivo']],
      body: tableData,
      startY: 20,
    });

    doc.save(`auditoria-promocao-saude-${competence}.pdf`);
  };

  const handleExportCsv = () => {
    const headers = ['Beneficiário', 'CPF', 'Valor', 'Motivo'];
    const rows = auditItems.map((item) => [
      item.beneficiaryName,
      item.beneficiaryCpf || '',
      (item.value || 0).toFixed(2).replace('.', ','),
      item.reason,
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(';')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `auditoria-promocao-saude-${competence}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatCurrency = (value: number): string =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <div className="space-y-6">
      <SubPageHeader
        backTo="/expenses/benefits/health-promotion"
        icon={<TrophyIcon className="h-5 w-5" aria-hidden />}
        badge="Promoção a saúde"
        title={`Competência ${competence}`}
        description="Prévia das importações e registros encontrados nesta competência."
      />

      <div className="rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Resumo</h2>
            <p className="text-sm text-gray-600">
              Total consolidado dos arquivos importados para a competência selecionada.
            </p>
          </div>
          {loading ? <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-500" aria-hidden /> : null}
        </div>

        {loadError ? (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{loadError}</div>
        ) : null}

        {!loading && !loadError && imports.length ? (
          <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-md border border-gray-200 px-4 py-3">
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Valor total</dt>
              <dd className="text-lg font-semibold text-gray-900">{formatCurrency(summary.totalAmount)}</dd>
            </div>
            <div className="rounded-md border border-gray-200 px-4 py-3">
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Assentos importados</dt>
              <dd className="text-lg font-semibold text-gray-900">{summary.totalSeats}</dd>
            </div>
            <div className="rounded-md border border-gray-200 px-4 py-3">
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Colaboradores vinculados</dt>
              <dd className="text-lg font-semibold text-gray-900">{summary.matchedCollaborators.size}</dd>
            </div>
          </dl>
        ) : null}
      </div>

      <div className="bg-white shadow sm:rounded-lg overflow-hidden border border-gray-200">
        <div
          className={`px-4 py-5 sm:px-6 border-b ${auditItems.length > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
            }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3
                className={`text-lg leading-6 font-medium flex items-center gap-2 ${auditItems.length > 0 ? 'text-red-800' : 'text-green-800'
                  }`}
              >
                {auditItems.length > 0 ? (
                  <ExclamationTriangleIcon className="h-5 w-5" />
                ) : (
                  <CheckCircleIcon className="h-5 w-5" />
                )}
                Auditoria Global ({auditItems.length})
              </h3>
              <p className={`mt-1 max-w-2xl text-sm ${auditItems.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {auditItems.length > 0
                  ? 'Inconsistências encontradas em todos os arquivos importados para esta competência.'
                  : 'Não foram encontradas pendências de auditoria no arquivo.'}
              </p>
            </div>
            {auditItems.length > 0 && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleExportCsv}
                  className="inline-flex items-center gap-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  <TableCellsIcon className="h-4 w-4 text-gray-500" />
                  CSV
                </button>
                <button
                  type="button"
                  onClick={handleExportPdf}
                  className="inline-flex items-center gap-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  <DocumentArrowDownIcon className="h-4 w-4 text-gray-500" />
                  PDF
                </button>
              </div>
            )}
          </div>
        </div>
        {auditItems.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Beneficiário
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Valor
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Motivo
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auditItems.map((item, idx) => (
                  <tr key={idx}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.beneficiaryName}
                      <div className="text-xs text-gray-500">{item.beneficiaryCpf}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatCurrency(item.value ?? 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                      {item.reason}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && !loadError && flattenedRecords.length > 0 ? (
        <div className="rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Colaboradores</h2>
              <p className="text-sm text-gray-600">Titulares importados, plano e vínculo com colaborador.</p>
            </div>
          </div>

          <div className="mt-4 overflow-hidden rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Titular</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">E-mail</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Plano</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {flattenedRecords.map((record) => (
                  <tr key={`${record.code}-${record.name}`}>
                    <td className="px-4 py-3 font-semibold text-gray-900">{record.name}</td>
                    <td className="px-4 py-3 text-gray-800">{record.email}</td>
                    <td className="px-4 py-3 text-gray-800">{record.planName}</td>
                    <td className="px-4 py-3 text-gray-800">{record.status}</td>
                    <td className="px-4 py-3 text-gray-900">{formatCurrency(record.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
